import { LocationStrategy } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, ViewChild, AfterViewInit, Injector, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Navigation, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription } from 'rxjs';
import { AlertType } from 'projects/shared/models/alert-type.enum';
import { Alert } from 'projects/shared/models/alert.model';
import { Borrower } from '../../models/borrower-model';
import { ErrorMessage } from '../../models/error-message.model';
import { ContextBoundStepProperties } from '../../models/wizard/context-bound-step-properties.model';
import { StepConfig } from '../../models/wizard/config/step-config.model';
import { WizardStepBase } from '../../models/wizard/wizard-step-base.model';
import { DataService } from '../../services/data.service';
import { MortgageApplicationService } from '../../services/mortgage-application.service';
import { UtilsService } from '../../services/utils.service';
import { WizardFlowConfigServiceBase } from '../../services/wizard/wizard-flow-config-service.base';
import { WizardFlowServiceBase } from '../../services/wizard/wizard-flow-service.base';
import { WizardStepTemplateComponent } from './wizard-step-template/wizard-step-template.component';
import { WizardFlowSettings } from '../../models/wizard/config/flow-settings.model';
import { localizeField } from '../../transloco/localize-field';
import { FIELD_LABEL_DEFAULTS } from '../../models/wizard/config/field-label-defaults';

declare const KTMenu: any;

@Component({
    template: '',
    standalone: false
})
export abstract class WizardStepComponentBase<
  TStep extends WizardStepBase,
  TStepConfig extends StepConfig = StepConfig
> implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild(WizardStepTemplateComponent) template: WizardStepTemplateComponent | undefined;

  @Output()
  wizardFlowSettingsChanged: EventEmitter<WizardFlowSettings> = new EventEmitter<WizardFlowSettings>();

  private readonly _location: LocationStrategy;

  protected wizardFlowService: WizardFlowServiceBase;
  protected wizardFlowConfigService: WizardFlowConfigServiceBase;
  protected mortgageApplicationService: MortgageApplicationService;
  protected utilsService: UtilsService;
  protected router: Router;
  protected dataService: DataService;
  protected spinner: NgxSpinnerService;
  protected cdr: ChangeDetectorRef;
  protected translocoService: TranslocoService;

  public step!: TStep;

  // The authorable StepConfig for this step, resolved alongside the runtime step.
  // Subclasses supply their concrete config type via the TStepConfig parameter
  // (defaults to StepConfig), so components read typed config without casts.
  protected config: TStepConfig | undefined;

  public contextBoundStepProperties!: ContextBoundStepProperties;

  public nextButtonText: string | undefined;

  public alert: Alert | undefined;

  protected saveMortgageApplicationBeforeNextStep: boolean = false;

  protected cameHereWithBackNavigation: boolean | undefined | null = false;

  private _editModeSub?: Subscription;

  constructor(protected readonly injector: Injector) {
    this.wizardFlowService = this.injector.get(WizardFlowServiceBase);
    this.wizardFlowConfigService = this.injector.get(WizardFlowConfigServiceBase);
    this.mortgageApplicationService = this.injector.get(MortgageApplicationService);
    this.router = this.injector.get(Router);
    this.dataService = this.injector.get(DataService);
    this.utilsService = this.injector.get(UtilsService);
    this.spinner = this.injector.get(NgxSpinnerService);
    this.cdr = this.injector.get(ChangeDetectorRef);
    this.translocoService = this.injector.get(TranslocoService);
    this._location = this.injector.get(LocationStrategy);
    const path = this.router.url.split('?')[0].split('/').pop();

    const navigation: Navigation = this.router.getCurrentNavigation();

    this.cameHereWithBackNavigation = (navigation &&
      navigation.extras &&
      navigation.extras.state &&
      navigation.extras.state.backNavigation);

    const step = this.wizardFlowService.getStep<TStep>(path);
    if (step) {
      this.step = step;
      // Additive (no behavior change): resolve this step's StepConfig alongside the
      // runtime step. Guarded so it can never break step construction; nothing reads
      // it yet. This is the wiring for the config-side-by-side refactor.
      try {
        const configService = this.injector.get(WizardFlowConfigServiceBase);
        // Single trust-boundary cast: getConfig is typed StepConfig|undefined; each subclass
        // declares its concrete TStepConfig. Replaces per-component casts at the read sites.
        this.config = configService.getConfig(this.wizardFlowService.activeFlowType, step) as TStepConfig | undefined;
      } catch {
        this.config = undefined;
      }
    }
    this._location.onPopState(() => {
      this.wizardFlowService.browserBackOrForwardClicked = true;
      return false;
    });
  }

  ngOnInit() {
    this.alert = undefined;
    this._editModeSub = this.wizardFlowService.editModeChanged$.subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  ngAfterViewInit() {
    KTMenu.createInstances();
    // Trigger change detection after view init to ensure @if blocks inside
    // ng-template + ngTemplateOutlet render properly in Angular 21+
    this.cdr.detectChanges();
    if (!this.template) {
      return;
    }
  }

  ngOnDestroy(): void {
    this._editModeSub?.unsubscribe();
  }

  onWizardFlowSettingsChanged = (settings: WizardFlowSettings) => {
    this.wizardFlowSettingsChanged.emit(settings);
  }

  isHidden = (fieldName: string): boolean => {
    return this.step.isHidden(fieldName);
  }

  isRequired = (fieldName: string): boolean => {
    return this.step.isRequired(fieldName);
  }

  getLabel = (fieldName: string, defaultLabel: string): string => {
    const fieldConfig = this.step.fieldConfig?.[fieldName];
    const lang = this.translocoService.getActiveLang();
    if (fieldConfig) {
      // 1. Admin _i18n override
      const adminOverride = lang ? fieldConfig._i18n?.[lang]?.displayName : undefined;
      if (adminOverride) {
        return adminOverride;
      }
      // 2. Built-in default for this language
      const builtIn = lang ? FIELD_LABEL_DEFAULTS[fieldName]?.[lang] : undefined;
      if (builtIn) {
        return builtIn;
      }
      // 3. English base
      return fieldConfig.displayName;
    }
    return defaultLabel;
  }

  public borrowerSetting(borrower: Borrower): boolean {
    const result = this.wizardFlowService.context.borrowerSettings.get(borrower.borrowerId);
    if (result) {
      return true;
    } else {
      return false
    }
  }

  protected borrowersToApplyFor(): Borrower[] {
    const borrowersToApplyFor: Borrower[] = [];
    borrowersToApplyFor.push(this.currentBorrower);
    if (this.inCoApplyFlow) {
      return borrowersToApplyFor;
    }
    const coBorrowers = this.mortgageApplication.borrowers.filter(b => b.primaryEmail.toLocaleLowerCase() !==
      this.currentBorrower.primaryEmail.toLocaleLowerCase());
    for (const b of coBorrowers) {
      if (this.borrowerSetting(b)) {
        borrowersToApplyFor.push(b);
      }
    }
    return borrowersToApplyFor;
  }

  get inCoApplyFlow() {
    return this.wizardFlowService.inCoApplyFlow;
  }

  // LLS-255: the flow-wide "Allow Borrower Enter Co-Borrower Personal Information" flag, stored once
  // on WizardFlowConfigs and edited from the My Info + Create Co-Borrowers step editors. Default true.
  get allowBorrowerEnterCoBorrowerPersonalInformation(): boolean {
    return this.wizardFlowConfigService.flowConfigs
      ?.allowBorrowerEnterCoBorrowerPersonalInformation ?? true;
  }
  set allowBorrowerEnterCoBorrowerPersonalInformation(value: boolean) {
    const flowConfigs = this.wizardFlowConfigService.flowConfigs;
    if (flowConfigs) {
      flowConfigs.allowBorrowerEnterCoBorrowerPersonalInformation = value;
    }
  }

  // The flag only applies to the Apply flow; never edit it from the Co-Apply flow.
  get canConfigureCoBorrowerPersonalInfo(): boolean {
    return !this.inCoApplyFlow;
  }

  // Runtime value passed to borrower-info-mini. In the Co-Apply flow the co-borrower is entering
  // their OWN info, so the flag never applies there — always behave as "true" (show all fields).
  get effectiveAllowBorrowerEnterCoBorrowerPersonalInformation(): boolean {
    return this.inCoApplyFlow ? true : this.allowBorrowerEnterCoBorrowerPersonalInformation;
  }

  public get inEditMode() {
    return this.wizardFlowService.inEditMode;
  }

  public get isEditEnabled() {
    return this.wizardFlowService.isEditEnabled;
  }

  protected get mortgageApplication() {
    return this.wizardFlowService.context.mortgage;
  }

  protected get applicationInfo() {
    return this.wizardFlowService.context.applicationInfo;
  }

  protected get currentBorrower() {
    return this.wizardFlowService.context.currentBorrower;
  }

  onBackClicked() {
    this.wizardFlowService.navigateBackward();
    window.scroll(0, 0);
  }

  onNextClicked() {
    if (this.saveMortgageApplicationBeforeNextStep && !this.wizardFlowService.isEditEnabled) {
      this.startSpinner();
      this.wizardFlowService.saveMortgage().subscribe(() => {
        this.stopSpinner();
        this.wizardFlowService.navigateForward();
        window.scroll(0, 0);
      }, err => {
        this.stopSpinner();
        const errorMessage = new ErrorMessage("An error occurred while saving the mortgage", err?.error?.message ?? err?.message);
        this.showError(errorMessage);
        window.scroll(0, 0);
      });
    } else {
      this.wizardFlowService.navigateForward();
      window.scroll(0, 0);
    }
  }

  public stopSpinner = (): void => {
    if (this.template) {
      this.template.stopSpinner();
    } else {
      this.spinner.hide();
    }
  }

  public startSpinner = (): void => {
    if (this.template) {
      this.template.startSpinner();
    } else {
      this.spinner.show();
    }
  }

  public showError = (error: ErrorMessage): void => {
    this.alert = new Alert(error.title, error.message, AlertType.Error);
  }

  public get canMoveToNextStep(): boolean {
    return true;
  }
}
